// NOTE: a sibling of this lives at apps/homepoint-api/src/utils/listingTimezone.ts.
// They share the STATE_TZ data but intentionally differ in shape: the backend
// resolves synchronously, this one is async because tz-lookup (~1MB) is
// dynamically imported so it code-splits out of the initial frontend bundle.

type ListingLike = {
  lat?: number | null;
  lng?: number | null;
  state?: string | null;
};

type TzLookup = (lat: number, lng: number) => string;

// tz-lookup ships a large embedded coordinate dataset; load it on demand
// (first booking-modal open) rather than in the initial bundle.
let tzlookupPromise: Promise<TzLookup> | null = null;
const loadTzLookup = (): Promise<TzLookup> => {
  if (!tzlookupPromise) {
    tzlookupPromise = import('tz-lookup').then((m) => m.default);
  }
  return tzlookupPromise;
};

const STATE_TZ: Record<string, string> = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage',
  AZ: 'America/Phoenix',
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DE: 'America/New_York',
  DC: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  HI: 'Pacific/Honolulu',
  ID: 'America/Boise',
  IL: 'America/Chicago',
  IN: 'America/Indiana/Indianapolis',
  IA: 'America/Chicago',
  KS: 'America/Chicago',
  KY: 'America/New_York',
  LA: 'America/Chicago',
  ME: 'America/New_York',
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/Detroit',
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  MT: 'America/Denver',
  NE: 'America/Chicago',
  NV: 'America/Los_Angeles',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NY: 'America/New_York',
  NC: 'America/New_York',
  ND: 'America/Chicago',
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  UT: 'America/Denver',
  VT: 'America/New_York',
  VA: 'America/New_York',
  WA: 'America/Los_Angeles',
  WV: 'America/New_York',
  WI: 'America/Chicago',
  WY: 'America/Denver',
};

const FALLBACK_TZ = 'America/Chicago';

const STATE_NAME_TO_CODE: Record<string, string> = {
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  'DISTRICT OF COLUMBIA': 'DC',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
};

const normalizeState = (state: string | null | undefined): string | null => {
  if (!state) return null;
  const trimmed = state.trim().toUpperCase();
  if (trimmed.length === 2) return trimmed;
  return STATE_NAME_TO_CODE[trimmed] ?? null;
};

export const getListingTimezone = async (
  listing: ListingLike
): Promise<string> => {
  const { lat, lng, state } = listing;
  if (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    (lat !== 0 || lng !== 0)
  ) {
    try {
      const tzlookup = await loadTzLookup();
      return tzlookup(lat, lng);
    } catch {
      // fall through to the state-code map
    }
  }
  const code = normalizeState(state);
  if (code && STATE_TZ[code]) return STATE_TZ[code];
  return FALLBACK_TZ;
};

/**
 * State-code-only resolution, synchronously — for formatting a date inline
 * during render (e.g. a contract date already normalized to the property's
 * timezone) where awaiting the lat/lng tz-lookup import isn't practical.
 * Coarser than getListingTimezone (no lat/lng leg) but sufficient: state is
 * exactly the granularity contract dates are normalized against.
 */
export const getListingTimezoneSync = (listing: {
  state?: string | null;
}): string => {
  const code = normalizeState(listing.state);
  if (code && STATE_TZ[code]) return STATE_TZ[code];
  return FALLBACK_TZ;
};
